import classcat from "classcat";
import React, { SyntheticEvent, useCallback, useContext, useMemo } from "react";

import { IconBasket, IconTick } from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import Price from "@is/components/Price";
import { MediaVariants, PackType, SELECTION_WIDGET_TYPES } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useContentItems } from "@is/hooks";
import { useManageBasket } from "@is/hooks/basket";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { ContentItemType, HTWIMedia, ProductItem, WidgetType } from "@is/types";

import css from "./HTWIWidgetItem.module.css";

type Props = {
  title?: string;
  items: ProductItem[];
  media: HTWIMedia[];
  mainStyle?: ProductItem;
  onScrollToTheItem: () => void;
  contentItemId?: string;
  mainVariantId?: string;
  onPDPItemClick?: () => void;
  autoCarousel?: boolean;
};

const HTWIWidgetItem = ({
  items,
  media,
  mainStyle,
  title = "",
  onScrollToTheItem,
  contentItemId,
  mainVariantId,
  onPDPItemClick,
  autoCarousel,
}: Props) => {
  const { updateViewState } = useContext(ViewStateContext).content;

  const { handleOpenProductDetails, openItemFromEntryView } = useContentItems();
  const { basketItems } = useManageBasket();

  const itemsMap = useMemo(() => {
    const map = new Map();

    items.forEach((item) => {
      map.set(item.id, { ...item });
    });

    return map;
  }, [items]);

  const itemsInTheBasket = useMemo(
    () => basketItems?.map((item) => item.productId) ?? [],
    [basketItems]
  );

  const handleQuickAddToBasket = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      const target = e.currentTarget as HTMLElement;
      const variantId = target.getAttribute("data-variant-id");

      const item = itemsMap.get(variantId);
      if (!item) return;

      trackEvent({
        eventType: TrackingEventTypes.QuickAddToBasketOpen,
        data: { id: item.id, styleId: item.styleId },
      });

      updateViewState({ quickAddToBasketItem: item });
      onScrollToTheItem();
    },
    [itemsMap, onScrollToTheItem, updateViewState]
  );

  const handleItemClick = useCallback(
    (e: SyntheticEvent) => {
      const target = e.currentTarget as HTMLElement;
      const variantId = target.getAttribute("data-id");

      const item = itemsMap.get(variantId);
      if (!item) return;

      trackEvent({
        eventType: SELECTION_WIDGET_TYPES[WidgetType.HowToWearIt],
        data: { variantId: item.id, styleId: item.styleId },
      });

      if (mainVariantId === variantId && onPDPItemClick) {
        onPDPItemClick();
      } else {
        handleOpenProductDetails({
          productItem: item,
          type: ContentItemType.WidgetItem,
          parentItemId: contentItemId,
        });
      }
    },
    [
      handleOpenProductDetails,
      contentItemId,
      itemsMap,
      mainVariantId,
      onPDPItemClick,
    ]
  );

  const handleAutoCarouselClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!mainStyle) return;

      trackEvent({
        eventType: SELECTION_WIDGET_TYPES[WidgetType.HowToWearIt],
        data: { variantId: mainStyle.id, styleId: mainStyle.styleId },
      });

      openItemFromEntryView(mainStyle);
    },
    [mainStyle, openItemFromEntryView]
  );

  return (
    <div
      className={classcat([
        css.container,
        { [css.autoCarousel]: autoCarousel },
      ])}
    >
      {title && !autoCarousel && (
        <div className={classcat([css.title, "text-label-2xl"])}>{title}</div>
      )}
      <div
        className={classcat([
          css.wrap,
          {
            [css.withTitle]: !!title && !autoCarousel,
            [css.autoCarouselWrap]: autoCarousel,
          },
        ])}
        {...(autoCarousel && { onClick: handleAutoCarouselClick })}
      >
        {media.map((item) => (
          <div
            key={item.id}
            className={classcat([
              css.item,
              {
                [css.modelShot]: item.packType === PackType.ModelShot,
                [css.itemsWithTitle]: !!title,
              },
            ])}
          >
            <div className={css.imageAndBasket}>
              <button
                className={classcat([
                  css.imageContainer,
                  {
                    [css.modelShot]: item.packType === PackType.ModelShot,
                    [css.bottom]: item.packType === PackType.BottomL1,
                    [css.imageWithTitle]: !!title,
                  },
                ])}
                data-id={item.variantId}
                onClick={handleItemClick}
              >
                <Image
                  src={item.url}
                  mediavariant={
                    item.packType === PackType.ModelShot
                      ? MediaVariants.LARGE_CROPPED_WITH_BACKGROUND_REMOVED
                      : MediaVariants.MEDIUM_CROPPED_WITH_BACKGROUND_REMOVED
                  }
                  placeholderMediaVariant={
                    MediaVariants.THUMBNAIL_CROPPED_WITH_BACKGROUND_REMOVED
                  }
                  showloadingspinner
                  layout="contain"
                />
              </button>

              {item.packType !== PackType.ModelShot && !autoCarousel && (
                <button
                  className={classcat([
                    css.basketIconContainer,
                    {
                      [css.addedToBasket]: itemsInTheBasket.includes(
                        item.styleId
                      ),
                      [css.notAddedToBasket]: !itemsInTheBasket.includes(
                        item.styleId
                      ),
                    },
                    "text-label-base",
                  ])}
                  data-variant-id={item.variantId}
                  onClick={handleQuickAddToBasket}
                >
                  {itemsInTheBasket.includes(item.styleId) ? (
                    <IconTick />
                  ) : (
                    <IconBasket />
                  )}
                </button>
              )}
            </div>

            {item.packType !== PackType.ModelShot && (
              <div className={css.infoWrapper}>
                <span className={classcat([css.styleName, css.ellipsis])}>
                  {itemsMap.get(item.variantId)?.name}
                </span>
                <span className={classcat([css.variantName, css.ellipsis])}>
                  {itemsMap.get(item.variantId)?.color}
                </span>
                {!!(
                  itemsMap.get(item.variantId)?.price?.retailPrice &&
                  itemsMap.get(item.variantId)?.price?.currencyCode
                ) && <Price price={itemsMap.get(item.variantId)?.price} />}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HTWIWidgetItem;
